import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";

import { useLiveStream } from "@/lib/livestream/use-livestream";
import { usePreferences } from "@/lib/preferences";
import {
  tempFromCelsius,
  tempUnitLabel,
  ecUnitLabel,
  levelFromGal,
  levelToGal,
  cycleLevelUnit,
  effectiveLevelUnit,
  levelUnitsAvailable,
  type LevelUnit,
  type ReservoirFactors,
} from "@/lib/units";
import { VESSEL_LABELS, VESSEL_SHORT_LABELS } from "@/lib/refill/vessel";
import type { Vessel } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormField } from "@/components/forms/field";
import { LiveMetric, fmt, trimNum } from "@/components/capture/live-metric";
import { cn } from "@/lib/utils";

// CaptureCard is the per-step pen-capture surface for the refill wizard. It
// shows the live pen reading large + glanceable, exposes a one-tap "Capture"
// that fills the editable fields from the latest sample, and keeps a manual
// override (the grower can dip-and-type when the pen isn't streaming).
//
// Values are held in *display* units to match the rest of the forms: pH and
// EC raw (EC is stored in mS/cm — the pen's native unit), temperature in the
// user's preferred unit. The wizard converts back to SI at the wire.

export interface CaptureValues {
  ph: string;
  ec: string;
  temp: string;
  level: string;
}

export const EMPTY_CAPTURE: CaptureValues = { ph: "", ec: "", temp: "", level: "" };

export function CaptureCard({
  values,
  onChange,
  withLevel = false,
  vessel,
  onVesselChange,
  reservoirFactors,
}: {
  values: CaptureValues;
  onChange: (next: CaptureValues) => void;
  withLevel?: boolean;
  // The vessel the pen is currently in. Optional so non-wizard callers still
  // compile; when supplied, the sticky "Pen is in" control is rendered and
  // every capture is tagged with this vessel by the caller.
  vessel?: Vessel;
  onVesselChange?: (v: Vessel) => void;
  // Reservoir geometry for depth↔gallons conversion on the level field. When
  // present (and the pen is in the reservoir), the level unit becomes tappable.
  reservoirFactors?: ReservoirFactors;
}) {
  const live = useLiveStream();
  const { temperatureUnit, ecUnit, levelUnit, setLevelUnit } = usePreferences();
  const sample = live.lastSample;
  const fresh = sample !== null;

  // values.level is always stored in GALLONS. The field is entered/displayed in
  // the user's chosen unit when reservoir geometry is known and the pen is in
  // the reservoir (the mixing batch has no geometry, so it stays gallons).
  const factors = reservoirFactors ?? {};
  // gal_per_inch and cm_per_gal are independent reservoir fields, so only offer
  // (and only ever store in) units this reservoir can actually convert. A unit
  // toggle is shown only when there's >1 convertible unit, and the effective
  // unit falls back to gal when the persisted preference can't be converted —
  // otherwise a depth in cm/in would be silently stored as gallons (~6.5x).
  const inReservoir = withLevel && vessel === "reservoir";
  const availableLevelUnits = inReservoir ? levelUnitsAvailable(factors) : (["gal"] as LevelUnit[]);
  const canConvertLevel = availableLevelUnits.length > 1;
  const effLevelUnit: LevelUnit = inReservoir
    ? effectiveLevelUnit(levelUnit, factors)
    : "gal";

  // Local display text so typing isn't clobbered by the gallons round-trip;
  // resync from stored gallons when it (or the unit) changes and we're idle.
  const [levelText, setLevelText] = useState("");
  const [levelFocused, setLevelFocused] = useState(false);
  useEffect(() => {
    if (levelFocused) return;
    setLevelText(
      values.level === ""
        ? ""
        : trimNum(levelFromGal(Number(values.level), effLevelUnit, factors)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.level, effLevelUnit, factors.galPerInch, factors.cmPerGal, levelFocused]);

  function onLevelText(raw: string) {
    setLevelText(raw);
    const n = parseFloat(raw);
    onChange({
      ...values,
      level: Number.isFinite(n) ? String(levelToGal(n, effLevelUnit, factors)) : "",
    });
  }

  function onCycleLevelUnit() {
    // Skip non-convertible units so we never land on a unit whose factor is
    // absent (which would store the depth as raw gallons).
    const next = cycleLevelUnit(effLevelUnit, factors);
    // values.level (gallons) is unchanged; just re-render the field in `next`.
    setLevelText(
      values.level === ""
        ? ""
        : trimNum(levelFromGal(Number(values.level), next, factors)),
    );
    setLevelUnit(next);
  }

  function capture() {
    if (!sample) return;
    const s = sample.sample;
    const ph = s.ph_filtered ?? s.ph;
    onChange({
      ...values,
      ph: typeof ph === "number" ? ph.toFixed(2) : values.ph,
      ec:
        typeof s.ec_ms_per_cm === "number"
          ? s.ec_ms_per_cm.toFixed(2)
          : values.ec,
      // Pen reports Celsius; fields hold the display unit.
      temp:
        typeof s.temperature_c === "number"
          ? tempFromCelsius(s.temperature_c, temperatureUnit).toFixed(1)
          : values.temp,
    });
  }

  const liveSample = sample?.sample;
  const livePh = liveSample
    ? (liveSample.ph_filtered ?? liveSample.ph)
    : undefined;

  // The live "pen … ago → 🪣 Mixing batch" hint always tells the user where
  // the system thinks the pen is, so a stray reading lands in the right vessel.
  const penHint = fresh
    ? `pen ${formatDistanceToNow(sample!.receivedAt, { addSuffix: true })}`
    : "no pen sample";

  return (
    <div className="space-y-3 rounded-md border p-4">
      {vessel !== undefined && onVesselChange && (
        <VesselControl vessel={vessel} onVesselChange={onVesselChange} />
      )}

      <div className="flex items-start justify-between gap-4">
        <dl className="flex flex-wrap gap-x-6 gap-y-1">
          <LiveMetric label="pH" value={fmt(livePh, 2)} />
          <LiveMetric
            label={ecUnitLabel(ecUnit)}
            value={fmt(liveSample?.ec_ms_per_cm, 2)}
          />
          <LiveMetric
            label={`temp ${tempUnitLabel(temperatureUnit)}`}
            value={
              typeof liveSample?.temperature_c === "number"
                ? tempFromCelsius(liveSample.temperature_c, temperatureUnit).toFixed(1)
                : "—"
            }
          />
        </dl>
        <div className="flex flex-col items-end gap-1">
          <Button type="button" size="sm" disabled={!fresh} onClick={capture}>
            Capture
          </Button>
          <span className="text-right text-[10px] text-muted-foreground">
            {vessel !== undefined
              ? `${penHint} → ${VESSEL_LABELS[vessel]}`
              : penHint}
          </span>
        </div>
      </div>

      <div className={"grid gap-3 " + (withLevel ? "grid-cols-4" : "grid-cols-3")}>
        <FormField label="pH">
          <Input
            type="number"
            step="any"
            value={values.ph}
            onChange={(e) => onChange({ ...values, ph: e.target.value })}
          />
        </FormField>
        <FormField label="EC (mS/cm)">
          <Input
            type="number"
            step="any"
            value={values.ec}
            onChange={(e) => onChange({ ...values, ec: e.target.value })}
          />
        </FormField>
        <FormField label={`temp ${tempUnitLabel(temperatureUnit)}`}>
          <Input
            type="number"
            step="any"
            value={values.temp}
            onChange={(e) => onChange({ ...values, temp: e.target.value })}
          />
        </FormField>
        {withLevel && (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                level
              </Label>
              {canConvertLevel ? (
                <button
                  type="button"
                  onClick={onCycleLevelUnit}
                  title="Tap to switch gal / in / cm"
                  className="text-xs uppercase tracking-wide text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
                >
                  ({effLevelUnit} ▾)
                </button>
              ) : (
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  (gal)
                </span>
              )}
            </div>
            <Input
              type="number"
              step="any"
              value={levelText}
              onFocus={() => setLevelFocused(true)}
              onBlur={() => setLevelFocused(false)}
              onChange={(e) => onLevelText(e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// VesselControl is the sticky "Pen is in:" segmented toggle. It surfaces the
// two primary vessels (Growing / Mixing) prominently with source water as a
// third, less-common option, so every capture is tagged with where the pen is.
function VesselControl({
  vessel,
  onVesselChange,
}: {
  vessel: Vessel;
  onVesselChange: (v: Vessel) => void;
}) {
  const options: Vessel[] = ["reservoir", "mixing_batch", "source_water"];
  return (
    <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-1 flex flex-wrap items-center gap-2 rounded-t-md border-b bg-background/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Pen is in:
      </span>
      <div className="inline-flex rounded-md border p-0.5" role="group">
        {options.map((opt) => {
          const active = opt === vessel;
          return (
            <button
              key={opt}
              type="button"
              aria-pressed={active}
              onClick={() => onVesselChange(opt)}
              className={cn(
                "rounded-sm px-2.5 py-1 text-xs font-medium transition-colors",
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              {VESSEL_SHORT_LABELS[opt]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

